/**
 * FlashBook — the unsaved-work fingerprint.
 *
 * Leaving the studio can still throw real work away — the recovery copy the
 * device keeps is this device's, and a draft is what makes a book real. So the
 * guard that protects it, and the snapshot that keeps it, both have to answer one
 * question: *has anything actually changed since the last save?*
 *
 * Answering it by remembering a boolean is how these guards go wrong in both
 * directions — a stray keystroke that was then undone marks the book dirty
 * forever, and simply opening a draft marks it dirty immediately because the
 * fields were populated. So instead we fingerprint the whole book and compare.
 * An untouched draft recomputes to the same string and the guard stays quiet;
 * any real edit changes it.
 *
 * Kept out of the screen because it is the kind of rule that should be provable
 * without rendering anything.
 */
import {
  type BackgroundMusicChoice,
  musicChoiceToRecord,
} from '#/lib/backgroundMusic'
import {canonicalContent} from '#/lib/contentSignature'

/** The part of a picked cover file the fingerprint cares about. */
type PickedMedia = {uri: string} | null | undefined

export type FlashBookContentSignatureInput = {
  title: string
  description: string
  themeId: string
  /**
   * The creator's music choice: a track id, `null` for deliberate silence, or
   * absent when nothing was ever chosen.
   *
   * All three are folded in, and the two "no music" states are folded in
   * *differently*: choosing silence is work — it is the creator taking the bed
   * away — and a signature that could not tell it from "unchosen" would let that
   * decision be lost on the way out of the studio without the guard ever waking.
   */
  backgroundMusicId?: BackgroundMusicChoice
  /** Only present for books built from a custom HTML theme. */
  htmlThemeUrl?: string
  cover: {
    kind: string
    image?: PickedMedia
    audio?: PickedMedia
    video?: PickedMedia
  }
  /** The pages and their blocks, as they sit in the studio. */
  pages: unknown
}

/**
 * A string that changes if — and only if — the book's content changes.
 *
 * `htmlThemeUrl` is folded in with `?? ''` so an absent theme and an empty one
 * are the same state. A draft opened from a book that never had a custom theme
 * must not read as "you have unsaved changes" the moment it loads.
 */
export function flashBookContentSignature(
  input: FlashBookContentSignatureInput,
): string {
  return JSON.stringify(
    canonicalContent([
      input.title,
      input.description,
      input.themeId,
      musicChoiceToRecord(input.backgroundMusicId) ?? '',
      input.htmlThemeUrl ?? '',
      input.cover.kind,
      input.cover.image?.uri ?? '',
      input.cover.audio?.uri ?? '',
      input.cover.video?.uri ?? '',
      input.pages,
    ]),
  )
}
